import React, { useMemo } from 'react';
import SearchSelect from '../../pages/SearchSelect';
import SearchPanel, { SearchField } from './SearchPanel';
import { SEARCH_GRID_CLASS, SEARCH_INPUT_CLASS } from './searchStyles';
import { fieldOptions, hasActiveDataFilters } from '../../utils/dataSearch';

const getLabel = (o) => o?.label ?? '';
const getValue = (o) => o?.value;

/**
 * Search panel for a master data table, configured by the table's columns (see utils/dataSearch
 * for the field format). Same look as TrialSearchFilters. Use with hooks/useDataSearch.
 */
export default function DataSearchFilters({ fields, rows, filters, onChange, onReset }) {
  // Dropdown values come from the loaded rows (no extra requests), unless the field has fixed choices
  const options = useMemo(() => Object.fromEntries(
    fields.filter((f) => f.type !== 'text' && !f.options).map((f) => [f.key, fieldOptions(rows, f)])
  ), [fields, rows]);
  const comboOptions = useMemo(() => Object.fromEntries(
    Object.entries(options).map(([key, values]) => [key, values.map((v) => ({ value: v, label: v }))])
  ), [options]);

  const input = (field) => {
    const value = filters[field.key] ?? '';
    if (field.type === 'select') {
      return (
        <select value={value} onChange={(e) => onChange({ [field.key]: e.target.value })} className={SEARCH_INPUT_CLASS}>
          {field.options
            ? field.options.map((o) => <option key={o.value} value={o.value}>{o.label ?? o.value}</option>)
            : [
              <option key="" value="">All</option>,
              ...options[field.key].map((o) => <option key={o} value={o}>{o}</option>),
            ]}
        </select>
      );
    }
    if (field.type === 'combobox') {
      return (
        <SearchSelect
          options={comboOptions[field.key]}
          value={value || null}
          onChange={(opt) => onChange({ [field.key]: opt ? opt.value : '' })}
          getOptionLabel={getLabel}
          getOptionValue={getValue}
          placeholder={field.placeholder ?? field.label}
          className="text-sm"
        />
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange({ [field.key]: e.target.value })}
        placeholder={field.placeholder ?? field.label}
        className={SEARCH_INPUT_CLASS}
      />
    );
  };

  return (
    <SearchPanel onReset={onReset} resetDisabled={!hasActiveDataFilters(filters, fields)}>
      <div className={SEARCH_GRID_CLASS}>
        {/* The free-text search only adds something when there is more than one column */}
        {fields.length > 1 && (
          <SearchField label="Search" className="sm:col-span-2 lg:col-span-4">
            <input
              type="search"
              value={filters.q ?? ''}
              onChange={(e) => onChange({ q: e.target.value })}
              placeholder="Search all columns..."
              title={`Searches ${fields.map((f) => f.label).join(', ')}`}
              className={SEARCH_INPUT_CLASS}
            />
          </SearchField>
        )}
        {fields.map((field) => (
          <SearchField key={field.key} label={field.label}>{input(field)}</SearchField>
        ))}
      </div>
    </SearchPanel>
  );
}
